import type { Project, ScenarioId } from '@finsim/model';
import { combine, createEffect, createEvent, createStore, sample } from 'effector';
import type { TFunction } from 'i18next';
import { $effectiveProject } from '../../entities/item-visibility';
import { toastShown } from '../../entities/notifications';
import { $locale } from '../../entities/preferences';
import { projectClosed } from '../../entities/project';
import {
  cancelReportFx,
  renderReportFx,
  ReportCancelledError,
  type ReportDocument,
} from '../../entities/report';
import {
  $mcResult,
  $mcRunning,
  $result,
  type MonteCarloResult,
  type SimulationResult,
} from '../../entities/simulation';
import { $hiddenScenarios, $simMode, $valueMode } from '../../entities/view-settings';
import type { Locale } from '../../shared/lib/locale';
import { defaultReportOptions, type ReportOptions, type ReportSectionKey } from './lib/options';

/*
 * The PDF report export (docs/PLAN.md M9). The dialog picks the options; exporting builds the
 * document here (strings, numbers and chart SVGs need the main thread's code), lays it out in the
 * report worker and downloads the file. The report describes the project as simulated (muted
 * items left out, like the charts). Monte Carlo ranges are included when a current Monte Carlo
 * result is on screen.
 */

export const reportDialogOpened = createEvent();
export const reportDialogClosed = createEvent();
export const optionsChanged = createEvent<Partial<Omit<ReportOptions, 'sections'>>>();
export const sectionToggled = createEvent<{ key: ReportSectionKey; on: boolean }>();
export const scenarioToggled = createEvent<{ id: ScenarioId; on: boolean }>();
/** `translate` gives the strings for the report's language (`i18n.getFixedT`). */
export const exportRequested = createEvent<{ translate: (locale: Locale) => TFunction }>();
export const exportCancelled = createEvent();
const progressed = createEvent<number>();

export const $reportOpen = createStore(false)
  .on(reportDialogOpened, () => true)
  .reset(reportDialogClosed, projectClosed);

/** A Monte Carlo result for the current plan: Monte Carlo mode, finished, same horizon. */
export const $mcForReport = combine(
  $simMode,
  $mcResult,
  $mcRunning,
  $result,
  (mode, mc, running, result): MonteCarloResult | null =>
    mode === 'monteCarlo' && !running && mc && mc.months === result?.months ? mc : null,
);

export const $options = createStore<ReportOptions | null>(null)
  .on(optionsChanged, (options, change) => (options ? { ...options, ...change } : options))
  .on(sectionToggled, (options, { key, on }) =>
    options ? { ...options, sections: { ...options.sections, [key]: on } } : options,
  )
  .on(scenarioToggled, (options, { id, on }) => {
    if (!options) return options;
    const ids = new Set(options.scenarioIds);
    if (on) ids.add(id);
    else ids.delete(id);
    return { ...options, scenarioIds: [...ids] };
  })
  .reset(projectClosed);

// Each time the dialog opens: everything included, for the variants shown in charts.
sample({
  clock: reportDialogOpened,
  source: {
    project: $effectiveProject,
    hidden: $hiddenScenarios,
    locale: $locale,
    valueMode: $valueMode,
  },
  filter: ({ project }) => project !== null,
  fn: ({ project, hidden, locale, valueMode }) =>
    defaultReportOptions(project as Project, hidden, locale, valueMode),
  target: $options,
});

export type ReportPhase = 'idle' | 'preparing' | 'rendering';

interface BuildParams {
  project: Project;
  result: SimulationResult;
  mc: MonteCarloResult | null;
  options: ReportOptions;
  t: TFunction;
}

/** File name: "Kasora – Rent vs buy – 2026-10-01.pdf" (characters files can't hold replaced). */
export function reportFileName(projectName: string, now: Date): string {
  const name = projectName.replace(/[\\/:*?"<>|]+/g, '-').trim();
  const date = `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return `Kasora – ${name} – ${date}.pdf`;
}

export const buildReportFx = createEffect(
  async ({ project, result, mc, options, t }: BuildParams) => {
    // Loaded on demand, with the server renderer the charts need, so the app's bundle stays lean.
    // The await also lets the dialog show "Preparing" before the synchronous build.
    const { buildReport } = await import('./lib/build');
    const now = new Date();
    const report: ReportDocument = buildReport({
      project,
      result,
      mc: options.sections.risk ? mc : null,
      options,
      t,
      now,
    });
    return { report, fileName: reportFileName(project.name, now) };
  },
);

export const downloadFx = createEffect(
  ({ pdf, fileName }: { pdf: Uint8Array; fileName: string }) => {
    const blob = new Blob([pdf as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    // Give the browser a moment to start the download before the URL goes.
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 10_000);
  },
);

const $fileName = createStore('').on(buildReportFx.doneData, (_, { fileName }) => fileName);

export const $phase = createStore<ReportPhase>('idle')
  .on(buildReportFx, () => 'preparing')
  .on(renderReportFx, () => 'rendering')
  .on(buildReportFx.fail, () => 'idle')
  .on(renderReportFx.finally, () => 'idle')
  .reset(projectClosed);
/** Share of the layout done while rendering. */
export const $progress = createStore(0)
  .on(progressed, (_, done) => done)
  .reset(buildReportFx);
export const $reportError = createStore<string | null>(null)
  .on(buildReportFx.failData, (_, error) => error.message)
  .on(renderReportFx.failData, (current, error) =>
    error instanceof ReportCancelledError ? current : error.message,
  )
  .reset(exportRequested, reportDialogOpened);

sample({
  clock: exportRequested,
  source: {
    project: $effectiveProject,
    result: $result,
    mc: $mcForReport,
    options: $options,
    phase: $phase,
  },
  filter: ({ project, result, options, phase }) =>
    project !== null &&
    result !== null &&
    options !== null &&
    options.scenarioIds.length > 0 &&
    phase === 'idle',
  fn: ({ project, result, mc, options }, { translate }) => {
    const opts = options as ReportOptions;
    return {
      project: project as Project,
      result: result as SimulationResult,
      mc,
      options: opts,
      t: translate(opts.locale),
    };
  },
  target: buildReportFx,
});

sample({
  clock: buildReportFx.doneData,
  fn: ({ report }) => ({ report, onProgress: (done: number) => progressed(done) }),
  target: renderReportFx,
});

sample({
  clock: renderReportFx.doneData,
  source: $fileName,
  fn: (fileName, pdf) => ({ pdf, fileName }),
  target: downloadFx,
});

sample({ clock: downloadFx.done, target: reportDialogClosed });
sample({
  clock: downloadFx.done,
  source: $fileName,
  fn: (fileName) => ({ message: 'report.dialog.saved' as const, params: { name: fileName } }),
  target: toastShown,
});

sample({ clock: [exportCancelled, reportDialogClosed, projectClosed], target: cancelReportFx });
